import React, { useState } from 'react';
import axios from 'axios';
import type { ReviewCycle, ChangeSpec } from './types';

const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:4000';

interface Props {
  cycle: ReviewCycle;
  // Needed to fetch the previous change spec — the API sits behind the auth
  // gate, so a plain <a href> gets "No valid authorization header".
  getAuthHeaders: () => Promise<Record<string, string>>;
}

/**
 * Spec §11.4 / Phase 5 — lineage banners shared by PreScreenView,
 * DecisionView and ReviewInterface.
 *   - a SUCCESSOR (cycle.revisionOf set) says what it claims to fix and how it
 *     re-entered, and can show the previous version's change spec inline;
 *   - a SUPERSEDED cycle says it is closed and kept as the record.
 * Renders nothing for an ordinary cycle.
 */
const RevisionBanner: React.FC<Props> = ({ cycle, getAuthHeaders }) => {
  const [spec, setSpec] = useState<ChangeSpec | null>(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  if (cycle.status === 'superseded') {
    return (
      <div data-testid="superseded-banner" style={{ margin: '4px 0 10px', padding: '8px 12px', borderRadius: 8, background: '#e9ecef', border: '1px solid #ced4da', fontSize: 13, color: '#495057' }}>
        <strong>Superseded</strong> — a newer version of this content was submitted
        {cycle.supersededAt ? ` on ${new Date(cycle.supersededAt).toLocaleDateString()}` : ''}. This cycle is closed and kept as the record; the current version carries the review.
      </div>
    );
  }
  const ac = cycle.amendment?.appliedCopy;
  const appliedNote = ac && !ac.skipped && ac.fileId ? (
    <div data-testid="applied-copy-banner" style={{ margin: '4px 0 10px', padding: '8px 12px', borderRadius: 8, background: '#eaf7ee', border: '1px solid #b7e1c1', fontSize: 13 }}>
      <strong>CellcoHub applied {ac.applied.length} text fix{ac.applied.length === 1 ? '' : 'es'}</strong> in the working file
      {ac.annotated.length ? `, and left ${ac.annotated.length} in-file comment${ac.annotated.length === 1 ? '' : 's'}` : ''}
      {ac.unanchored.length ? ` (${ac.unanchored.length} could not be anchored — see the change spec)` : ''}.
      {' '}{ac.webUrl ? <a href={ac.webUrl} target="_blank" rel="noreferrer">{ac.fileName || 'Open working file'}</a> : <span>{ac.fileName}</span>}
      {cycle.contentType === 'docx' ? <span style={{ color: '#6c757d' }}> — direct edits, each with a comment; a tracked-changes copy sits beside it.</span> : <span style={{ color: '#6c757d' }}> — direct edits, each with a slide comment.</span>}
    </div>
  ) : (ac && ac.skipped && cycle.status === 'revision_needed' ? (
    <div data-testid="applied-copy-skipped" style={{ margin: '4px 0 10px', padding: '6px 12px', borderRadius: 8, background: '#f8f9fa', border: '1px solid #e9ecef', fontSize: 12, color: '#6c757d' }}>
      In-file apply not performed: {ac.reason}. The change spec lists everything to do by hand.
    </div>
  ) : null);
  if (!cycle.revisionOf) return appliedNote;

  const n = (cycle.addresses || []).length;
  const dm = cycle.reentry === 'dm';

  const toggleSpec = async () => {
    if (open) { setOpen(false); return; }
    if (spec) { setOpen(true); return; }
    try {
      setBusy(true); setErr(null);
      const headers = await getAuthHeaders();
      const r = await axios.get(`${backendUrl}/api/content-review/cycles/${cycle.revisionOf}/change-spec`, { headers });
      setSpec(r.data);
      setOpen(true);
    } catch (e: any) {
      setErr(e.response?.data?.error || 'Could not load the previous change spec');
    } finally {
      setBusy(false);
    }
  };

  const copy = async () => {
    if (!spec?.markdown) return;
    try { await navigator.clipboard.writeText(spec.markdown); setCopied(true); setTimeout(() => setCopied(false), 1800); } catch { /* no clipboard */ }
  };

  return (<>
    {appliedNote}
    <div data-testid="revision-banner" style={{ margin: '4px 0 10px', padding: '8px 12px', borderRadius: 8, background: dm ? '#eaf7ee' : '#fff3cd', border: `1px solid ${dm ? '#b7e1c1' : '#ffe08a'}`, fontSize: 13 }}>
      <strong>Revision</strong> of the previous version — claims to address {n} finding{n === 1 ? '' : 's'}.
      {' '}{dm ? 'No critical/major fixes: straight to decision — fast-track is appropriate.' : 'Re-enters review: every step must be completed again.'}
      {cycle.reentryDetail ? <span style={{ color: '#6c757d' }}> ({cycle.reentryDetail})</span> : null}
      {' '}
      <button onClick={toggleSpec} disabled={busy} style={{ background: 'none', border: 'none', padding: 0, marginLeft: 6, color: '#123250', textDecoration: 'underline', cursor: 'pointer', fontSize: 13 }}>
        {busy ? 'Loading…' : open ? 'Hide previous change spec' : 'Previous change spec'}
      </button>
      {err && <div style={{ color: '#dc3545', marginTop: 6 }}>{err}</div>}
      {open && spec && (
        <div style={{ marginTop: 8, border: '1px solid #d0d7de', borderRadius: 8, padding: 10, background: '#fafbfc' }}>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 6 }}>
            <span style={{ fontWeight: 600, color: '#123250' }}>{spec.label}</span>
            <span style={{ color: '#6c757d' }}>{spec.apply.length} to apply · {spec.alreadyApplied.length} already applied · {spec.wontApply.length} won't apply</span>
            <button className="review-btn review-btn-outline" style={{ fontSize: 12, padding: '2px 8px', marginLeft: 'auto' }} onClick={copy}>{copied ? 'Copied' : 'Copy'}</button>
          </div>
          <textarea readOnly value={spec.markdown} style={{ width: '100%', minHeight: 140, fontFamily: 'monospace', fontSize: 12, border: '1px solid #e1e4e8', borderRadius: 6, padding: 8, background: '#fff', boxSizing: 'border-box' }} />
        </div>
      )}
    </div>
  </>);
};

export default RevisionBanner;
